import GlobalStyles from '@mui/material/GlobalStyles';
import {
  memo,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { tintOnWhite } from '@/theme/colorUtils';
import { r, radii } from '../../theme/tokens';
import { getUiTokens, type UiTone } from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { buildTickLabelInterval } from './chartTickLabelInterval';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { StackedBarChartRoundedTopBar } from './StackedBarChartRoundedTopBar';
import { StackedTotalsTooltip } from './StackedBarChartTooltip';
import { MuiBarChart } from './xCharts';
import type { BarItemIdentifier, ChartsAxisData } from '@mui/x-charts/models';

export type StackedBarChartDatum = Record<string, string | number>;

type StackedBarSeries = { key: string; label: string; color?: string; colorKey?: UiTone };

type StackedBarChartProps = {
  data: StackedBarChartDatum[];
  series: StackedBarSeries[];
  height?: number;
  xAxisLabelFormatter?: (label: string) => string;
  tooltipLabelFormatter?: (label: string) => string;
  hideXAxisTicks?: boolean;
  xAxisLabelOffset?: number;
  yAxisLabelOffset?: number;
  axisStrokeWidth?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  yAxisWidth?: number;
  yAxisAutoWidth?: boolean;
  yAxisAutoWidthPadding?: number;
  barCornerRadius?: number;
  stackId?: string;
  skipAnimation?: boolean;
  showHorizontalGrid?: boolean;
  showVerticalGrid?: boolean;
  hideXAxisLine?: boolean;
  onSelectBar?: (dataIndex: number, seriesKey?: string) => void;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
};

const StackedBarChartBase = ({
  data,
  series,
  height = 260,
  xAxisLabelFormatter = formatChartDateLabel,
  tooltipLabelFormatter = formatChartDateLabel,
  hideXAxisTicks = false,
  xAxisLabelOffset = 4,
  yAxisLabelOffset = -6,
  axisStrokeWidth = 1,
  marginLeft = -8,
  marginRight = 16,
  marginTop = 16,
  marginBottom = 14,
  yAxisWidth = 72,
  yAxisAutoWidth = false,
  yAxisAutoWidthPadding = 12,
  barCornerRadius = radii.md,
  stackId = 'total',
  skipAnimation = false,
  showHorizontalGrid = true,
  showVerticalGrid = false,
  hideXAxisLine = true,
  onSelectBar,
  disableInteraction = false,
  disableTooltip = false,
}: StackedBarChartProps): JSX.Element => {
  const categoryGapRatio = chartBarSizing.default.categoryGapRatio;
  const barGapRatio = chartBarSizing.default.barGapRatio;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeResetTimerRef = useRef<number | null>(null);
  const [suspendAnimationForResize, setSuspendAnimationForResize] = useState(false);
  const [resolvedYAxisWidth, setResolvedYAxisWidth] = useState(yAxisWidth);
  const [plotBounds, setPlotBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const tokens = getUiTokens(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const barRadius = barCornerRadius;
  const lastItemClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const lastItemClickTimeoutRef = useRef<number | null>(null);
  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: BarItemIdentifier) => {
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const seriesKey = item.seriesId ? String(item.seriesId) : undefined;
      lastItemClickRef.current = { dataIndex, timestamp: Date.now() };
      if (lastItemClickTimeoutRef.current !== null) {
        window.clearTimeout(lastItemClickTimeoutRef.current);
      }
      lastItemClickTimeoutRef.current = window.setTimeout(() => {
        lastItemClickRef.current = null;
        lastItemClickTimeoutRef.current = null;
      }, 600);
      onSelectBar?.(dataIndex, seriesKey);
    },
    [onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      const target = _event.target;
      if (target && target instanceof Element && target.closest('.MuiBarElement-root')) {
        return;
      }
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const lastItemClick = lastItemClickRef.current;
      if (lastItemClick && Date.now() - lastItemClick.timestamp < 600) {
        return;
      }
      onSelectBar?.(dataIndex);
    },
    [onSelectBar],
  );
  const formatWholeNumber = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    return Number.isInteger(value) ? String(value) : '';
  };
  const formatTooltipValue = (value: number | null): string | null => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return null;
    return formatWholeNumber(value);
  };
  const palette = [
    tokens.statusColors.purple,
    tokens.statusColors.green,
    tokens.statusColors.orange,
    tokens.statusColors.blue,
    tokens.statusColors.red,
  ];
  const segmentSeparatorColor = theme.palette.background.paper;
  const segmentSeparatorWidth = 1;
  const resolveBarColor = (base: string): string =>
    theme.palette.mode === 'dark' ? darken(base, 0.26) : tintOnWhite(base, 0.7);
  const tooltipFontSize = theme.typography.subtitle1.fontSize ?? '15px';
  const tooltipLineHeight = theme.typography.subtitle1.lineHeight ?? 1.4;
  const tooltipFontWeight = theme.typography.fontWeightRegular ?? 400;
  const rawLabels = data.map((item) => String(item.label));
  const xLabels = rawLabels.map((label) => xAxisLabelFormatter(label));
  const tooltipLabels = data.map((item) => tooltipLabelFormatter(String(item.label)));
  const xTickLabelInterval = buildTickLabelInterval(xLabels.length);
  const adjustedMarginLeft = marginLeft + (resolvedYAxisWidth - yAxisWidth);
  const seriesKeys = series.map((item) => item.key);
  const totalsByLabel = new Map<string, number>();
  const tooltipLabelByAxisLabel = new Map<string, string>();
  data.forEach((row, index) => {
    const axisLabel = xLabels[index];
    const tooltipLabel = tooltipLabels[index];
    if (axisLabel && tooltipLabel) {
      tooltipLabelByAxisLabel.set(axisLabel, tooltipLabel);
    }
    const label = tooltipLabel;
    if (!label) return;
    const total = seriesKeys.reduce((sum, key) => {
      const value = Number(row[key]);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    totalsByLabel.set(label, total);
  });
  const topSeriesKeyByRow = data.map((row) => {
    let topKey: string | null = null;
    seriesKeys.forEach((key) => {
      const value = Number(row[key]);
      if (!Number.isFinite(value) || value === 0) return;
      topKey = key;
    });
    return topKey;
  });
  const resolveSeriesColor = (item: StackedBarSeries, index: number): string =>
    resolveBarColor(
      item.color ??
        (item.colorKey ? tokens.statusColors[item.colorKey] : palette[index % palette.length]),
    );
  const effectiveSkipAnimation = skipAnimation || suspendAnimationForResize;

  useEffect(() => {
    if (!yAxisAutoWidth) {
      setResolvedYAxisWidth(yAxisWidth);
      return;
    }
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updateYAxisWidth = (): void => {
      const labels = container.querySelectorAll(
        '.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel',
      );
      if (labels.length === 0) return;
      let maxLabelWidth = 0;
      labels.forEach((label) => {
        if (!(label instanceof SVGGraphicsElement)) return;
        const bounds = label.getBBox();
        if (Number.isFinite(bounds.width) && bounds.width > maxLabelWidth) {
          maxLabelWidth = bounds.width;
        }
      });
      const extraLeftGutter = Math.max(0, -marginLeft);
      const nextWidth = Math.max(
        yAxisWidth,
        Math.ceil(
          maxLabelWidth + Math.abs(yAxisLabelOffset) + yAxisAutoWidthPadding + extraLeftGutter,
        ),
      );
      setResolvedYAxisWidth((currentWidth) =>
        Math.abs(currentWidth - nextWidth) > 1 ? nextWidth : currentWidth,
      );
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updateYAxisWidth);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
    };
  }, [
    data,
    marginLeft,
    series,
    yAxisAutoWidth,
    yAxisAutoWidthPadding,
    yAxisLabelOffset,
    yAxisWidth,
  ]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    let previousWidth = element.clientWidth;
    let previousHeight = element.clientHeight;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      const width = entry.contentRect.width;
      const heightValue = entry.contentRect.height;
      if (width === previousWidth && heightValue === previousHeight) return;
      previousWidth = width;
      previousHeight = heightValue;

      setSuspendAnimationForResize(true);
      if (resizeResetTimerRef.current !== null) {
        window.clearTimeout(resizeResetTimerRef.current);
      }
      resizeResetTimerRef.current = window.setTimeout(() => {
        setSuspendAnimationForResize(false);
        resizeResetTimerRef.current = null;
      }, 180);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (resizeResetTimerRef.current !== null) {
        window.clearTimeout(resizeResetTimerRef.current);
        resizeResetTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updatePlotBounds = (): void => {
      const svg = container.querySelector('svg');
      if (!(svg instanceof SVGSVGElement)) return;
      const yAxisLine = container.querySelector(
        '.MuiChartsAxis-directionY .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisLine = container.querySelector(
        '.MuiChartsAxis-directionX .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      if (!xAxisLine) return;
      const svgRect = svg.getBoundingClientRect();
      const xRect = xAxisLine.getBoundingClientRect();
      const yRect = yAxisLine?.getBoundingClientRect();
      const rawLeft = yRect ? yRect.left - svgRect.left : xRect.left - svgRect.left;
      const rawTop = yRect ? yRect.top - svgRect.top : 16;
      const rawRight = xRect.right - svgRect.left;
      const rawBottom = xRect.top - svgRect.top;
      const svgWidth = svgRect.width;
      const svgHeight = svgRect.height;
      const left = Math.max(0, Math.min(rawLeft, svgWidth));
      const top = Math.max(0, Math.min(rawTop, svgHeight));
      const right = Math.max(left, Math.min(rawRight, svgWidth));
      const bottom = Math.max(top, Math.min(rawBottom, svgHeight));
      const width = Math.max(0, right - left);
      const plotHeight = Math.max(0, bottom - top);
      setPlotBounds({ left, top, width, height: plotHeight });
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updatePlotBounds);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [adjustedMarginLeft, data, height, resolvedYAxisWidth, series, suspendAnimationForResize]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'hidden',
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': { color: theme.app.colors.divider },
      }}
    >
      {plotBounds ? (
        <Box
          sx={{
            position: 'absolute',
            left: plotBounds.left,
            top: plotBounds.top,
            width: plotBounds.width,
            height: plotBounds.height,
            backgroundColor: theme.palette.background.paper,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ) : null}
      <GlobalStyles
        styles={{
          '.flowqa-stacked-tooltip-paper': {
            borderRadius: r('md'),
            border: `1px solid ${theme.app.colors.border}`,
            boxShadow: 'none',
            backgroundColor: theme.app.colors.surface,
            color: theme.app.colors.text,
            paddingTop: 6,
            paddingBottom: 6,
            paddingLeft: 4,
            paddingRight: 4,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            fontWeight: theme.typography.fontWeightRegular,
          },
          '.flowqa-stacked-tooltip-paper *': {
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            fontWeight: theme.typography.fontWeightRegular,
          },
          '.flowqa-stacked-tooltip-table': {
            borderSpacing: 0,
            border: '0 !important',
            width: 'auto !important',
            tableLayout: 'auto',
          },
          '.flowqa-stacked-tooltip-table > thead > tr > th, .flowqa-stacked-tooltip-table > thead > tr > td':
            {
              borderBottom: `1px solid ${theme.app.colors.border} !important`,
              paddingTop: '0px',
              paddingBottom: '4px',
              paddingLeft: '4px !important',
              paddingRight: '4px !important',
              lineHeight: 1.05,
              textAlign: 'left',
              verticalAlign: 'middle !important',
            },
          '.flowqa-stacked-tooltip-table > thead > tr > th': {
            fontWeight: theme.typography.fontWeightRegular,
          },
          '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-label':
            {
              textAlign: 'right !important',
              whiteSpace: 'nowrap',
            },
          '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-value':
            {
              textAlign: 'right !important',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            },
          '.flowqa-stacked-tooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > th, .flowqa-stacked-tooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > td':
            {
              borderBottom: '0 !important',
              paddingBottom: '0 !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr > th, .flowqa-stacked-tooltip-table > tbody > tr > td':
            {
              border: '0 !important',
              paddingLeft: '6px !important',
              paddingRight: '6px !important',
              verticalAlign: 'middle !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, .flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
            {
              paddingTop: '6px !important',
              textAlign: 'left !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr:first-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:first-of-type > td':
            {
              paddingTop: '8px !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr:last-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:last-of-type > td':
            {
              paddingBottom: '4px !important',
            },
          '.flowqa-stacked-tooltip-table .MuiChartsTooltip-markContainer': {
            width: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
          },
          '.flowqa-stacked-tooltip-cell': {
            paddingTop: '2px',
            paddingBottom: '2px',
            lineHeight: 1.1,
          },
          '.flowqa-stacked-tooltip-count-cell': {
            textAlign: 'right !important',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          },
          '.flowqa-stacked-tooltip-percent-cell': {
            textAlign: 'right !important',
            whiteSpace: 'nowrap',
            minWidth: '40px',
            fontVariantNumeric: 'tabular-nums',
          },
          '.flowqa-stacked-tooltip-mark': {
            marginTop: 0,
            marginBottom: 0,
          },
          '.flowqa-stacked-tooltip-paper.flowqa-stacked-tooltip-paper-empty': {
            paddingBottom: 4,
          },
        }}
      />
      <MuiBarChart
        height={height}
        hideLegend
        skipAnimation={effectiveSkipAnimation}
        borderRadius={barRadius}
        slots={{
          bar: (props) => (
            <StackedBarChartRoundedTopBar
              {...props}
              topSeriesKeyByRow={topSeriesKeyByRow}
              barRadius={barRadius}
              segmentSeparatorColor={segmentSeparatorColor}
              segmentSeparatorWidth={segmentSeparatorWidth}
            />
          ),
          tooltip: (tooltipProps) => (
            <StackedTotalsTooltip
              {...tooltipProps}
              totalsByLabel={totalsByLabel}
              tooltipLabelByAxisLabel={tooltipLabelByAxisLabel}
              tooltipLabelsByIndex={tooltipLabels}
              tooltipLabelFormatter={tooltipLabelFormatter}
            />
          ),
        }}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        xAxis={[
          {
            data: rawLabels,
            scaleType: 'band',
            categoryGapRatio,
            barGapRatio,
            valueFormatter: (value) => xAxisLabelFormatter(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: `translateY(${xAxisLabelOffset}px)`,
            },
            tickSize: hideXAxisTicks ? 0 : 6,
          },
        ]}
        yAxis={[
          {
            min: 0,
            width: resolvedYAxisWidth,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: `translateX(${yAxisLabelOffset}px)`,
            },
            tickSize: 0,
            valueFormatter: formatWholeNumber,
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: showVerticalGrid }}
        margin={{
          left: adjustedMarginLeft,
          right: marginRight,
          top: marginTop,
          bottom: marginBottom,
        }}
        slotProps={{
          axisLine: { stroke: theme.app.colors.divider },
          axisTick: { stroke: theme.app.colors.divider },
          tooltip: {
            classes: {
              paper: 'flowqa-stacked-tooltip-paper',
              table: 'flowqa-stacked-tooltip-table',
              cell: 'flowqa-stacked-tooltip-cell',
              mark: 'flowqa-stacked-tooltip-mark',
            },
            sx: {
              fontSize: tooltipFontSize,
              lineHeight: tooltipLineHeight,
              fontWeight: tooltipFontWeight,
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: '6px !important',
              paddingRight: '6px !important',
              '& .MuiChartsTooltip-table > thead > tr > th, & .MuiChartsTooltip-table > thead > tr > td, & table > thead > tr > th, & table > thead > tr > td':
                {
                  borderBottom: `1px solid ${theme.app.colors.border} !important`,
                  boxShadow: 'none !important',
                  paddingLeft: '4px !important',
                  paddingRight: '4px !important',
                  paddingTop: '0 !important',
                  paddingBottom: '4px !important',
                  textAlign: 'left',
                  verticalAlign: 'middle !important',
                },
              '& .MuiChartsTooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > th, & .MuiChartsTooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > td, & table.flowqa-stacked-tooltip-table-empty > thead > tr > th, & table.flowqa-stacked-tooltip-table-empty > thead > tr > td':
                {
                  borderBottom: '0 !important',
                  paddingBottom: '0 !important',
                },
              '& .MuiChartsTooltip-table, & table': {
                borderCollapse: 'separate',
                borderSpacing: 0,
                width: 'auto !important',
                tableLayout: 'auto',
              },
              '& .MuiChartsTooltip-markContainer': {
                width: '20px !important',
                display: 'inline-flex !important',
                alignItems: 'center !important',
                verticalAlign: 'middle !important',
              },
              '& .MuiChartsTooltip-row, & tr': {
                lineHeight: '1.1 !important',
              },
              '& .MuiTypography-root, & .MuiChartsTooltip-cell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell, & .MuiChartsTooltip-axisValueCell, & caption, & td, & th':
                {
                  color: theme.app.colors.text,
                  fontSize: tooltipFontSize,
                  lineHeight: tooltipLineHeight,
                  fontWeight: tooltipFontWeight,
                  textAlign: 'left !important',
                },
              '& .MuiChartsTooltip-cell, & .MuiChartsTooltip-markCell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell':
                {
                  paddingTop: '2px !important',
                  paddingBottom: '2px !important',
                  lineHeight: '1.1 !important',
                  verticalAlign: 'middle !important',
                },
              '& .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, & .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
                {
                  paddingTop: '6px !important',
                  textAlign: 'left !important',
                },
              '& .MuiChartsTooltip-table > tbody > tr:first-of-type > th, & .MuiChartsTooltip-table > tbody > tr:first-of-type > td':
                {
                  paddingTop: '8px !important',
                },
              '& .MuiChartsTooltip-table > tbody > tr:last-of-type > th, & .MuiChartsTooltip-table > tbody > tr:last-of-type > td':
                {
                  paddingBottom: '4px !important',
                },
              '& .flowqa-stacked-tooltip-count-cell, & .flowqa-stacked-tooltip-percent-cell': {
                textAlign: 'right !important',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              },
              '& .flowqa-stacked-tooltip-percent-cell': {
                minWidth: '40px',
              },
              '& .MuiChartsTooltip-mark': {
                marginTop: '0 !important',
                marginBottom: '0 !important',
              },
              '& .flowqa-stacked-tooltip-header-total-label': {
                textAlign: 'right !important',
                whiteSpace: 'nowrap',
              },
              '& .flowqa-stacked-tooltip-header-total-value': {
                textAlign: 'right !important',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              },
              '& .MuiChartsTooltip-axisValue, & .MuiChartsTooltip-axisValueCell': {
                marginTop: '0 !important',
                marginBottom: '0 !important',
                paddingTop: '0 !important',
                paddingBottom: '0 !important',
                lineHeight: '1.05 !important',
              },
            },
          },
        }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
            strokeWidth: axisStrokeWidth,
          },
          '& .MuiChartsGrid-horizontalLine': { stroke: theme.app.colors.divider },
          '& .MuiChartsAxis-line': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
            strokeWidth: axisStrokeWidth,
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            hideXAxisLine
              ? {
                  stroke: 'transparent !important',
                }
              : {
                  stroke: `${theme.app.colors.divider} !important`,
                },
          '& .MuiChartsAxisHighlight-root, & .MuiChartsAxisHighlight': {
            pointerEvents: 'none',
          },
          '& .MuiChartsAxisHighlight-root *': {
            pointerEvents: 'none',
          },
          '& .MuiChartsAxisHighlight-rect, & .MuiChartsAxisHighlightRect': {
            fill: `${theme.palette.action.hover} !important`,
            opacity: 0.18,
          },
          '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlightLine': {
            stroke: 'transparent !important',
            opacity: 0,
          },
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
          ...(disableTooltip
            ? {
                '& .MuiChartsTooltip-root': { display: 'none' },
                '& .MuiChartsTooltip-paper': { display: 'none' },
              }
            : null),
        }}
        series={series.map((item, index) => ({
          id: item.key,
          data: data.map((row) => Number(row[item.key])),
          label: item.label,
          color: resolveSeriesColor(item, index),
          stack: stackId,
          valueFormatter: (value) => formatTooltipValue(value),
        }))}
      />
    </Box>
  );
};

export const StackedBarChart = memo(StackedBarChartBase);
